<template>
  <div class="user-activity">
    <div class="user-activity-toolbar">
      <v-col cols="2">
        <OrgSelect v-model="selectedOrg" :disabled="loading" @orgName="handleOrgName" @orgsLoaded="setOrgList"/>
      </v-col>

      <!-- date select -->
      <v-col cols="3">
        <div class="date-picker">
          <material-date-time-range 
            :start-value="startValue" 
            :local-time="true"
            @change="range => selectDateRange = range">
          </material-date-time-range>
        </div>
      </v-col>

      <!-- users select -->
      <v-col cols="2">
        <SelectUsers v-model="selectedUsers" :users="users" :loading="loading || !users.length" />
      </v-col>

      <!-- filters select -->
      <v-col cols="2">
        <SelectFilters v-model="selectedFilters" :loading="loading" />
      </v-col>

      <!-- search field -->
      <v-col cols="2">
        <v-text-field v-model="search" append-icon="mdi-magnify" label="Search" single-line hide-details />
      </v-col>

      <div class="actions">
        <v-icon :disabled="!allowActions" style="margin-right: 10px" @click="getTableData"> mdi-refresh </v-icon>
        <v-icon :disabled="!allowActions" style="margin-right: 10px" @click="download">mdi-download</v-icon>
      </div>
    </div>  

    <v-data-table
      :headers="headers"
      :items="report"
      :search="search"
      item-key="key"
      :loading="loading"
      loading-text="Loading... Please wait"
      :items-per-page="-1"
    >   
    
      <template v-slot:item.info="{ item }">
        <div v-html="item.info"></div>
      </template>

    </v-data-table>
  </div>
</template>

<script>
import { Logger } from "aws-amplify"
import {
  ListOrgUsers,
  GetUserActivityFilters
} from "@/services/API.js"
import { 
  parseCSV,
  addTimeOffsets
} from "@/utils";
import OrgSelect from "@/components/general/OrgSelect"
import SelectUsers from "./SelectUsers.vue"
import SelectFilters from "./SelectFilters.vue";
import moment from "moment"
import xlsx from 'xlsx'
import { AmplifyEventBus } from "aws-amplify-vue"
import { userActivity } from '@/mixins/user-activity.js' 

const logger = new Logger("User Activity Report")

export default {
  components: {
    OrgSelect,
    SelectUsers,
    SelectFilters
  },
  mixins: [userActivity],
  data () {
    return {
      startValue: [
        moment().startOf("day").subtract(7, 'days').format("YYYY-MM-DDTHH:mm:ss"),
        moment().endOf("day").format("YYYY-MM-DDTHH:mm:ss")
      ],
      selectDateRange: [
        moment().startOf("day").subtract(7, 'days').format("YYYY-MM-DDTHH:mm:ss"),
        moment().endOf("day").format("YYYY-MM-DDTHH:mm:ss")
      ],
      selectedOrg: false,
      orgName: '',
      tableData: [],
      loadingTimeout: false, // we use this to clear any loading in the event of an error since some of the loading is non observable
      loading: false,
      search: '',
      selectedFilters: ["login","retrieve_video","access_video","delete_video","export_video","access_alert_video","export_alert_video","stream_live_video","stream_recorded_video","share_video", "unshare_video"], // types of user activity to filter the API query with
      selectedUsers: [], 
      orgList: [],
      headers: [
        {
          "text": "User",
          "value": "user_name",
          "align": "left"
        },
        {
          "text": "Activity",
          "value": "activity",
          "align": "left"
        },
        {
          "text": "Time (UTC)",
          "value": "activity_time",
          "align": "left"
        },
        {
          "text": "Status",
          "value": "activity_status",
          "align": "left"
        },
        {
          "text": "Additional Information",
          "value": "info",
          "align": "left"
        }
      ],
      users: [],
      report: [],
    }
  },
  computed: {
    dateRange () {
      const offset = moment().format("Z") // tz offset, no dst
      const convertToUtc = addTimeOffsets(offset, '+00:00', true) // flipped offset to go from local to UTC

      return this.selectDateRange.map(ts => `${moment.utc(ts).utcOffset(convertToUtc).format('YYYY-MM-DDTHH:mm:ss')}Z`)
    },
    allowActions() {
      return this.report.length > 0 ? true : false
    }
  },
  watch: {
    selectedOrg() {
      this.$nextTick(() => {
        this.getTableData()
        this.report = [] // reset tableData
        this.selectedUsers = []
      })
    },
    selectDateRange() {
      if(this.selectedOrg) {
        this.$nextTick(() => {
          this.getTableData()
        })
      }
    },
    selectedUsers () {
      if(this.selectedOrg) {
        this.$nextTick(() => {
          this.getTableData()
        })
      }
    },
    selectedFilters () {
      if(this.selectedOrg) {
        this.$nextTick(() => {
          this.getTableData()
        })
      }
    },
  },
  methods: {
    setOrgList (orgs) {
      this.orgList = orgs
    },

    download () {
      if (this.report.length > 0) {
        // create our excel file
        const wb = xlsx.utils.book_new()

        logger.debug("download data", this.report)
        
        let data = this.report.map(i => {
          // parse out the html elements into text for the export
          try {
            const info = new DOMParser().parseFromString(i.info, 'text/html')
              
            return {
              User: i.user_name,
              Activity: i.activity,
              'Time (UTC)': i.activity_time,
              Status: i.activity_status,
              'Additional Infomation': info.body.textContent || ""
            }
          }

          catch(err) {
            logger.debug("error", err)
            return {
              User: 'test'
            }
          }
        })

        logger.debug("download data 2", data)

        const sheet = xlsx.utils.json_to_sheet(data)
        const reportName = `${this.orgName}_activity`

        // apend them to the workbook
        xlsx.utils.book_append_sheet(wb, sheet, reportName)

        xlsx.writeFile(wb, reportName + ".xlsx")
      }
    },
    async getTableData() {
      this.loading = true
      this.report = []

      try {
        const userList = await ListOrgUsers(this.selectedOrg, true)

        if (userList && userList.Users.length > 0) {
          this.users = userList.Users.map((user) => {
            user.name = `${user.Profile.firstname} ${user.Profile.lastname}`

            return user
          })
          
          const emails = userList.Users.map((user) => {
            user.status = !user.disabled // we want the switch to be off if the user is disabled
            return user.email
          })

          const usersToSend = this.selectedUsers && this.selectedUsers.length
            ? this.selectedUsers.map(i => i.email)
            : emails

          const filtersToSend = this.selectedFilters && this.selectedFilters.length
           ? this.selectedFilters
           : false

          const reportURL = await GetUserActivityFilters(this.selectedOrg, usersToSend, this.dateRange, filtersToSend)

          if(reportURL) {
            const parsedData = await this.parseCSV(reportURL)

            logger.debug("parsed data", parsedData)

            if(parsedData) {
              try {
                this.report = parsedData.map(item => ({
                  user_name: this.mapEmailToName(item.entity_id),
                  activity: this.$getUserActivity(item),
                  activity_time: moment(item.timestamp).format("YYYY-MM-DD HH:mm:ss"),
                  activity_status: this.$getUserActivityStatus(item), 
                  info: this.$getUserActivityDescription(item, this.orgList, this.selectedOrg) // selected org is the users home org on this page
                })).sort((a, b) => new Date(b.activity_time) - new Date(a.activity_time))
              }

              catch (err) {
                logger.debug("couldn't map data", err)
                this.report = []
              }
            }

            
          } else {
            logger.debug("could not load data", reportURL)
          }

          this.loading = false
        }
        else {
          this.loading = false
          AmplifyEventBus.$emit("snackEvent", {
            color: "error",
            text: 'There are no users in the selected organisation'
          })

        }

      } catch {
        AmplifyEventBus.$emit("snackEvent", {
          color: "error",
          text: 'An error occurred - please try again later'
        })
        this.loading = false
      }
        
      
    },
    handleOrgName(name) {
      this.orgName = name;
    }, 
    mapEmailToName(email) {
      const user = this.users.find(u => u.email === email);
      return user ? user.name : email;
    },
    parseCSV(data) {
      return new Promise(async (res, rej)  => {
       try {
          const trackingFile = await fetch(data)
          const trackingData = await trackingFile.text()
          logger.debug("parseCSV1", JSON.stringify(trackingData))
          const parsedData = parseCSV(trackingData)
          logger.debug('parseCSV2', data, JSON.parse(JSON.stringify(parsedData)));
          res(parsedData)
       } catch (err) {  
        AmplifyEventBus.$emit("snackEvent", {
          color: "error",
          text: 'An error occurred - please try again later'
        });
        rej(err)
       }
      });
    },

    showSelectFilters () {
      alert('show filters')
    }
  }
}
</script>

<style lang="scss" scoped>
.user-activity {
  padding: 1em;
  .user-activity-toolbar {
    display: flex; 
    align-items: baseline; 
  }
  .date-picker {
    top: 40px;
    position: absolute;
  }
  .actions {
    position: absolute;
    right: 20px;
    top: 10px;
  }
}

.cm-filter-select {
  display: flex;

  p {
    padding: 0 !important;
    margin: 0 0 0 10px !important;
    font-weight: bold !important;
  }
}
</style>